import { useCallback } from 'react';
import { useAccount } from 'wagmi';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { buildApiUrl, recaptchaSiteKey } from '@/lib/globals';

const recaptchaAssessmentUrl = buildApiUrl('recaptcha');
const featureFlagUrl = buildApiUrl('feature-flags');

type FeatureFlags = {
    incubatorOpen?: boolean;
    quizPreview?: boolean;
};

export type RecaptchaResult = 'ALLOWED' | 'NOT_ALLOWED' | 'ERROR';
type UseOnlybotsBackend = {
    fetchRecaptchaAssessment: () => Promise<RecaptchaResult>;
    getFeatureFlags: () => Promise<FeatureFlags>;
};

export const useOnlybotsBackend = (): UseOnlybotsBackend => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { address } = useAccount();

    const getFeatureFlags = useCallback(async (): Promise<FeatureFlags> => {
        try {
            const response = await fetch(featureFlagUrl);
            return await response.json();
        } catch (e) {
            console.error(e);
            return {};
        }
    }, []);

    const requestAuthorization = useCallback(
        async (url: URL, body: RequestInit): Promise<RecaptchaResult> => {
            if (!address) {
                throw new Error('No active wallet');
            }

            try {
                const response = await fetch(url, body);
                if (response.status === 401) {
                    return 'NOT_ALLOWED';
                }
                if (response.ok) {
                    return 'ALLOWED';
                }

                return 'ERROR';
            } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(
                        'For local development, you must have onlybots-functions running to get a recaptcha score.'
                    );
                }
                console.error(e);
                return 'ERROR';
            }
        },
        [address]
    );

    const fetchRecaptchaAssessment = useCallback(async (): Promise<RecaptchaResult> => {
        if (!executeRecaptcha) {
            throw new Error('Recaptcha not ready');
        }
        try {
            const token = await executeRecaptcha('VERIFY');
            return await requestAuthorization(recaptchaAssessmentUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify({
                    walletAddress: address,
                    recaptcha: {
                        token,
                        recaptchaSiteKey,
                    },
                }),
            });
        } catch (e) {
            console.error(e);
            return 'ERROR';
        }
    }, [address, executeRecaptcha, requestAuthorization]);

    return {
        fetchRecaptchaAssessment,
        getFeatureFlags,
    };
};
